// Per-slicer settings model + resolution helpers shared by the dashboard
// builder (ReportBuilderPage) and viewer (ReportViewPage). Stored on the
// report/dashboard config under `config.slicerSettings` keyed by dimension.

export const DISPLAY_TYPES = [
  { key: 'dropdown', label: 'Dropdown' },
  { key: 'multi', label: 'Multi-Select' },
  { key: 'buttons', label: 'Buttons' },
  { key: 'radio', label: 'Radio List' },
  { key: 'search', label: 'Searchable' },
  { key: 'text_match', label: 'Text filter' },
  { key: 'date_picker', label: 'Date Picker' },
  { key: 'date_range', label: 'Date Range' },
  { key: 'number_range', label: 'Number Range' },
  { key: 'calendar', label: 'Calendar (date)' },
];

export const DEFAULT_TYPES = [
  { key: 'all', label: 'All / None (no filter)' },
  { key: 'first', label: 'First record' },
  { key: 'last', label: 'Last record' },
  { key: 'newest', label: 'Newest / most-recent value' },
  { key: 'relative', label: 'Relative date range', dateOnly: true },
  { key: 'top_measure', label: 'Top value by a measure' },
  { key: 'chosen', label: 'Chosen value(s) at build time' },
  { key: 'profile', label: "Viewer's profile value" },
  { key: 'remember', label: "Remember viewer's last selection" },
];

export const RELATIVE_OPTIONS = [
  { key: 'last_7_days', label: 'Last 7 days' },
  { key: 'last_30_days', label: 'Last 30 days' },
  { key: 'last_90_days', label: 'Last 90 days' },
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'this_quarter', label: 'This quarter' },
  { key: 'this_year_to_date', label: 'Year to date' },
  { key: 'this_year', label: 'This year' },
  { key: 'last_year', label: 'Last year' },
];

export const DEFAULT_SLICER_SETTING = {
  displayType: null,          // null = legacy/auto (date→calendar, else dropdown)
  selectionMode: 'single',    // 'single' | 'multiple'
  showAll: true,
  defaultSelection: { type: 'remember', relativePreset: 'last_30_days', measure: '', values: [], profileAttr: '' },
  sortOrder: 'data',          // 'data' | 'asc' | 'desc'
  customTitle: '',
  locked: false,
  sync: false,                // keep all slicers for this field in sync across pages/visuals
  scope: null,                // null/[] = affects all visuals; array of visual ids = only those
  cascade: true,              // narrow this slicer's values by other slicers' selections
};

export function getSlicerSetting(all, dim) {
  const s = (all && all[dim]) || {};
  return {
    ...DEFAULT_SLICER_SETTING,
    ...s,
    defaultSelection: { ...DEFAULT_SLICER_SETTING.defaultSelection, ...(s.defaultSelection || {}) },
  };
}

function cmp(a, b) {
  const na = Number(a), nb = Number(b);
  if (a !== '' && b !== '' && !isNaN(na) && !isNaN(nb)) return na - nb;
  const da = Date.parse(a), db = Date.parse(b);
  if (!isNaN(da) && !isNaN(db)) return da - db;
  return String(a).localeCompare(String(b));
}

export function sortSlicerValues(values, sortOrder) {
  const arr = (values || []).map(v => v);
  if (sortOrder === 'asc') return arr.sort(cmp);
  if (sortOrder === 'desc') return arr.sort((a, b) => cmp(b, a));
  return arr; // 'data' — natural/as-returned order
}

const iso = (d) => d.toISOString().split('T')[0];

export function relativePresetToRange(preset) {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth();
  let s, e = now;
  switch (preset) {
    case 'last_7_days': s = new Date(now - 7 * 86400000); break;
    case 'last_30_days': s = new Date(now - 30 * 86400000); break;
    case 'last_90_days': s = new Date(now - 90 * 86400000); break;
    case 'this_month': s = new Date(y, m, 1); break;
    case 'last_month': s = new Date(y, m - 1, 1); e = new Date(y, m, 0); break;
    case 'this_quarter': s = new Date(y, Math.floor(m / 3) * 3, 1); break;
    case 'this_year_to_date': s = new Date(y, 0, 1); break;
    case 'this_year': s = new Date(y, 0, 1); break;
    case 'last_year': s = new Date(y - 1, 0, 1); e = new Date(y - 1, 11, 31); break;
    default: s = new Date(now - 30 * 86400000);
  }
  return `${iso(s)}|${iso(e)}`;
}

const REMEMBER_PREFIX = 'slicer_last_';
export function rememberKey(reportId, dim) { return `${REMEMBER_PREFIX}${reportId || 'r'}_${dim}`; }

export function readRememberedSelection(reportId, dim) {
  try {
    const raw = localStorage.getItem(rememberKey(reportId, dim));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

export function writeRememberedSelection(reportId, dim, value) {
  try {
    if (value === null || value === undefined) localStorage.removeItem(rememberKey(reportId, dim));
    else localStorage.setItem(rememberKey(reportId, dim), JSON.stringify(value));
  } catch { /* ignore */ }
}

// Resolve the open-state selection for a slicer given its setting + available
// values. Returns the selection value (string | array | 'start|end' | null).
// `top_measure` needs aggregated data and is resolved asynchronously by the
// caller (resolveTopValue); if `topValue` is provided it is used here.
export function resolveDefaultSelection({ setting, values, isDate, reportId, dim, topValue, userAttrs }) {
  const ds = setting.defaultSelection || { type: 'all' };
  const sorted = sortSlicerValues(values, setting.sortOrder);
  const multi = setting.selectionMode === 'multiple';
  const wrap = (v) => (v == null ? null : (multi ? [String(v)] : String(v)));
  switch (ds.type) {
    case 'first': return sorted.length ? wrap(sorted[0]) : null;
    case 'last': return sorted.length ? wrap(sorted[sorted.length - 1]) : null;
    case 'newest': {
      if (!values || !values.length) return null;
      const max = [...values].sort(cmp)[values.length - 1];
      return wrap(max);
    }
    case 'relative':
      return isDate ? relativePresetToRange(ds.relativePreset || 'last_30_days') : null;
    case 'top_measure':
      return topValue != null ? wrap(topValue) : null;
    case 'chosen': {
      const chosen = (ds.values || []).map(String).filter(v => (values || []).map(String).includes(v));
      if (!chosen.length) return null;
      return multi ? chosen : chosen[0];
    }
    case 'remember': {
      const r = readRememberedSelection(reportId, dim);
      return r === undefined ? null : r;
    }
    case 'profile': {
      // Default to the viewer's own attribute value (e.g. their region).
      const raw = userAttrs ? (userAttrs[ds.profileAttr] ?? (userAttrs.attributes || {})[ds.profileAttr]) : null;
      if (raw == null || raw === '') return null;
      const match = (values || []).map(String).find(v => v.toLowerCase() === String(raw).toLowerCase());
      return match != null ? (multi ? [match] : match) : wrap(raw);
    }
    case 'all':
    default:
      return null;
  }
}
